// Memberships & passes, club admin side (MEMBERSHIPS_PLAN.md §6). The network
// half: every verb endpoint behind one function with the API's reason surfaced
// as a plain string, plus the per-club "is it set up yet" state the nav chip
// and dashboard read. Pure wording and maths live in utils/memberships.js.
import {computed, ref} from 'vue';
import {useState} from '#app';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints';
import {formatMinor} from '~/utils/adminActionCopy';
import {TYPE_STATUS_INT, APPLICATION_STATUS_INT} from '~/utils/memberships';

/** The API's reason for a refusal, or a fallback. Crud errors arrive as {global: [...]} or {field: [...]}. */
export function membershipApiError(e, fallback = 'Something went wrong. Please try again') {
  if (!e) return fallback;
  if (typeof e === 'string') return e;
  if (Array.isArray(e.global) && e.global.length) return String(e.global[0]);
  if (Array.isArray(e.generalErrors) && e.generalErrors.length) return String(e.generalErrors[0]);
  if (e.title && typeof e.title === 'string') return e.title;
  if (e.message && typeof e.message === 'string') return e.message;
  const firstField = Object.values(e).find(v => Array.isArray(v) && v.length);
  if (firstField) return String(firstField[0]);
  return fallback;
}

/** "£240.00" / "£12.50", the server's own money style (always two decimals since X62). */
export function membershipMoney(minor, currency) {
  return formatMinor(minor ?? 0, currency);
}

/**
 * Per-club setup state: whether any type is Live (the nav item shows "Set up"
 * until one is), and how many applications wait. Loaded by the admin layout
 * when the club has memberships switched on; pages refresh it after a change.
 */
export function useMembershipsSetup() {
  const state = useState('membershipsSetup', () => ({clubId: null, liveCount: null, typeCount: null, pendingApplications: null}));
  const {httpRequest} = useHttpRequest();

  const load = async (clubId) => {
    if (!clubId) return;
    try {
      const [live, all, pending] = await Promise.all([
        httpRequest(endpoints.memberships.types.getItems, {page: 1, limit: 1, filters: {clubIds: [clubId], statuses: [TYPE_STATUS_INT.Live]}}),
        httpRequest(endpoints.memberships.types.getItems, {page: 1, limit: 1, filters: {clubIds: [clubId]}}),
        httpRequest(endpoints.memberships.applications.getItems, {page: 1, limit: 1, filters: {clubIds: [clubId], statuses: [APPLICATION_STATUS_INT.Pending]}}),
      ]);
      state.value = {
        clubId,
        liveCount: live?.totalItems ?? live?.total ?? (live?.items?.length ?? 0),
        typeCount: all?.totalItems ?? all?.total ?? (all?.items?.length ?? 0),
        pendingApplications: pending?.totalItems ?? pending?.total ?? (pending?.items?.length ?? 0),
      };
    } catch {
      // An old API or a blip: the nav simply shows no chip rather than a wrong one.
      state.value = {clubId, liveCount: null, typeCount: null, pendingApplications: null};
    }
  };

  // X226: memberships are on for every club by default, so "Set up" marks a club that started (a type exists) with
  // nothing live yet; a club that never made one is not nagged (the setup guide's Memberships group still asks when ticked).
  const needsSetup = computed(() => state.value.liveCount === 0 && (state.value.typeCount ?? 0) > 0);
  const pendingApplications = computed(() => state.value.pendingApplications ?? 0);
  return {membershipsSetup: state, loadMembershipsSetup: load, needsSetup, pendingApplications};
}

export function useMembershipsAdmin() {
  const {httpRequest} = useHttpRequest();
  const pending = ref(false);

  const call = async (endpoint, body) => {
    pending.value = true;
    try {
      return await httpRequest(endpoint, body ?? {});
    } finally {
      pending.value = false;
    }
  };

  // ── Types ──
  const getType = (id) => httpRequest(endpoints.memberships.types.getItem(id));
  const listTypes = (clubId, extraFilters = {}, limit = 100) =>
      httpRequest(endpoints.memberships.types.getItems, {page: 1, limit, filters: {clubIds: [clubId], ...extraFilters}});
  const createType = (body) => call(endpoints.memberships.types.addItem, body);
  const updateType = (id, body) => call(endpoints.memberships.types.updateItem(id), body);
  const setTypeStatus = (id, status, visibility) => call(endpoints.memberships.types.status(id), {status, visibility});
  const archiveType = (id) => call(endpoints.memberships.types.archive(id));
  const unarchiveType = (id) => call(endpoints.memberships.types.unarchive(id));
  const quickAddType = (body) => call(endpoints.memberships.types.quickAdd, body);
  const suggestions = (clubId) => httpRequest(endpoints.memberships.types.suggestions(clubId));
  const adjustPeriod = (typeId, periodId, body) => call(endpoints.memberships.types.adjustPeriod(typeId, periodId), body);
  const generatePeriods = (typeId) => call(endpoints.memberships.types.generatePeriods(typeId));
  const acceptTemplateUpdate = (typeId, terms, medical) => call(endpoints.memberships.types.acceptTemplateUpdate(typeId, terms, medical));
  const makeOwnMedicalCopy = (typeId) => call(endpoints.memberships.types.makeOwnMedicalCopy(typeId));

  // ── Templates (club admins read the published ones) ──
  const publishedTerms = (kind, sportId) => httpRequest(endpoints.memberships.templates.terms(kind, sportId, true));
  const publishedQuestionnaires = (sportId) => httpRequest(endpoints.memberships.templates.questionnaires(sportId, true));

  // ── Memberships ──
  const listMemberships = (clubId, extraFilters = {}, limit = 1000) =>
      httpRequest(endpoints.memberships.memberships.getItems, {page: 1, limit, filters: {clubIds: [clubId], ...extraFilters}});
  const getMembership = (id) => httpRequest(endpoints.memberships.memberships.getItem(id));
  const adminAdd = (body) => call(endpoints.memberships.memberships.adminAdd, body);
  const cancelMembership = (id, now, reason, refundMinor = null) =>
      call(endpoints.memberships.memberships.cancel(id), {now: !!now, reason: reason || null, refundMinor: now ? (refundMinor ?? null) : null});
  const cancelPreview = (id) => httpRequest(endpoints.memberships.memberships.cancelPreview(id));
  const resumeMembership = (id) => call(endpoints.memberships.memberships.resume(id));
  const setAutoRenew = (id, autoRenew) => call(endpoints.memberships.memberships.updateItem(id), {id, autoRenew});

  // ── Health (D24) ──
  const healthAccess = (clubId) => httpRequest(endpoints.memberships.memberships.healthAccess(clubId));
  const healthAccessChanges = (clubId) => httpRequest(endpoints.memberships.memberships.healthAccessChanges(clubId));
  const setHealthAccess = (clubId, playerId, granted) => call(endpoints.memberships.memberships.setHealthAccess, {clubId, playerId, granted});
  const applicationHealth = (applicationId) => httpRequest(endpoints.memberships.applications.health(applicationId));
  const membershipHealth = (responseId) => httpRequest(endpoints.memberships.memberships.health(responseId, 'web-admin'));
  const healthViews = (responseId) => httpRequest(endpoints.memberships.memberships.healthViews(responseId));

  // ── Applications ──
  const listApplications = (clubId, extraFilters = {}, limit = 200) =>
      httpRequest(endpoints.memberships.applications.getItems, {page: 1, limit, filters: {clubIds: [clubId], ...extraFilters}});
  const getApplication = (id) => httpRequest(endpoints.memberships.applications.getItem(id));
  const approveApplication = (id) => call(endpoints.memberships.applications.approve(id));
  const declineApplication = (id, reason) => call(endpoints.memberships.applications.decline(id), {reason: reason || null});
  const resendParentConsent = (id, body) => call(endpoints.memberships.applications.resendParentConsent(id), body ?? {});

  return {
    pending,
    getType, listTypes, createType, updateType, setTypeStatus, archiveType, unarchiveType, quickAddType, suggestions,
    adjustPeriod, generatePeriods, acceptTemplateUpdate, makeOwnMedicalCopy,
    publishedTerms, publishedQuestionnaires,
    listMemberships, getMembership, adminAdd, cancelMembership, cancelPreview, resumeMembership, setAutoRenew,
    healthAccess, healthAccessChanges, setHealthAccess, applicationHealth, membershipHealth, healthViews,
    listApplications, getApplication, approveApplication, declineApplication, resendParentConsent,
  };
}
